"use client"

import { useEffect, useRef, useState } from "react"
import { useLocale, useTranslations } from "next-intl"
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes"
import { useAuth } from "@/hooks/use-auth"
import { useQueryClient } from "@tanstack/react-query"
import { Card } from "@/components/ui/card"
import { WizardStepActions } from "./wizard-step-actions"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { RoleNameStep, type RoleNameValues } from "./role-name-step"
import { KnowledgeStep } from "./knowledge-step"
import { PromptReviewForm } from "@/components/agents/shared/personalization/prompt-review-form"
import { SharedVoiceConfigurationForm, type VoiceConfigurationValues } from "@/components/agents/shared/personalization/voice-configuration-form"
import type { SignupOnboardingPromptApproval } from "@/components/onboarding/agent-name-personalization"
import { OnboardingStepActionsProvider } from "@/components/onboarding/onboarding-step-actions"
import { PersonalizationProgress } from "@/components/onboarding/personalization-progress"
import { usePrepareAgentCreationMutation, usePersonalizeAgentCreationMutation, useRegenerateAgentCreationMutation, useCreateWizardAgentMutation, useWizardVoiceOpeningMutation } from "@/hooks/mutations/agent-creation"
import { defaultAgentLanguages, withAgentLanguages, type CreationAgentType } from "@/lib/agents/creation/types"
import { formatSignupOnboardingBusinessSummary } from "@/lib/onboarding/signup-onboarding"

type WizardStep = "role" | "knowledge" | "personalization" | "voice"
type Personalization = { prompt: string; summary: string | null; languages: string[]; opening: string | null }
type WizardProps = { type: CreationAgentType; onCancel: () => void; onCreated: (id: string) => void }
export function AgentCreationWizard(props: WizardProps) {
  const { user } = useAuth()
  const tenantId = user?.impersonated_tenant_id ?? user?.tenant_id
  return <WizardInstance key={`${user?.user_id}:${tenantId}:${props.type}`} {...props} />
}

function WizardInstance({ type, onCancel, onCreated }: WizardProps) {
  const t = useTranslations("AgentCreation")
  const locale = useLocale()
  const client = useQueryClient()
  const prepare = usePrepareAgentCreationMutation()
  const personalize = usePersonalizeAgentCreationMutation()
  const regenerate = useRegenerateAgentCreationMutation()
  const create = useCreateWizardAgentMutation()
  const opening = useWizardVoiceOpeningMutation()
  const [step, setStep] = useState<WizardStep>("role")
  const [identity, setIdentity] = useState<RoleNameValues>({ name: "", roles: [], roleLabel: "" })
  const [draft, setDraft] = useState<{ draftId: string; originalPrompt: string; identity: RoleNameValues } | null>(null)
  const [documentIds, setDocumentIds] = useState<string[]>([])
  const [knowledgeMode, setKnowledgeMode] = useState<"selected" | "skipped">("skipped")
  const [result, setResult] = useState<Personalization | null>(null)
  const [voiceDraft, setVoiceDraft] = useState<VoiceConfigurationValues | null>(null)
  const [approval, setApproval] = useState<SignupOnboardingPromptApproval | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const revision = useRef(0)
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; revision.current += 1 } }, [])
  const dirty = identity.name.length > 0 || draft !== null
  useUnsavedChanges(dirty, create.isPending, t("discard"))
  const leave = () => {
    if (dirty && !window.confirm(t("discard"))) return
    revision.current += 1
    onCancel()
  }
  const back = () => {
    if (result && !window.confirm(t("discardPrompt"))) return
    revision.current += 1
    setResult(null)
    setError(null)
    setBusy(false)
    setStep("knowledge")
  }
  const generate = async (mode: "selected" | "skipped") => {
    if (!draft) return
    const current = ++revision.current
    setStep("personalization")
    setKnowledgeMode(mode)
    setError(null)
    setResult(null)
    setVoiceDraft(null)
    setBusy(true)
    try {
      if (mode === "selected") {
        const generated = await personalize.mutateAsync({ draftId: draft.draftId, documentIds })
        if (current !== revision.current || !mounted.current) return
        setResult({ prompt: generated.enriched_prompt, summary: formatSignupOnboardingBusinessSummary(generated.business_facts), languages: generated.languages, opening: generated.opening_message })
      } else {
        const languages = defaultAgentLanguages(locale)
        const generatedOpening = type === "voice" ? await opening.mutateAsync({ draftId: draft.draftId, language: languages[0] }) : null
        if (current !== revision.current || !mounted.current) return
        setResult({ prompt: withAgentLanguages(draft.originalPrompt, languages), summary: null, languages, opening: generatedOpening?.text ?? null })
      }
    } catch (error) {
      if (current === revision.current && mounted.current) setError(error instanceof Error ? error.message : t("failed"))
    } finally {
      if (current === revision.current && mounted.current) setBusy(false)
    }
  }
  const submit = async (approved: SignupOnboardingPromptApproval, voice?: VoiceConfigurationValues) => {
    if (!draft) return
    const saved = await create.mutateAsync({ draftId: draft.draftId, name: approved.agentName, prompt: approved.prompt, languages: approved.languages, documentIds: knowledgeMode === "selected" ? documentIds : [], voice })
    await client.invalidateQueries({ queryKey: ["agent-creation-knowledge"] })
    onCreated(saved.agentId)
  }
  let surface
  switch (step) {
    case "role":
      surface = (
        <RoleNameStep
          type={type}
          values={identity}
          onChange={setIdentity}
          onNext={async values => {
            if (draft && draft.identity.roles.join(",") === values.roles.join(",") && draft.identity.roleLabel === values.roleLabel) {
              setStep("knowledge")
              return
            }
            const prepared = await prepare.mutateAsync({ type, ...values, roleLabel: values.roleLabel || null })
            setIdentity(values)
            setDraft({ ...prepared, identity: values })
            setResult(null)
            setStep("knowledge")
          }}
        />
      )
      break
    case "knowledge":
      surface = (
        <KnowledgeStep
          type={type}
          selected={documentIds}
          onChange={setDocumentIds}
          onNext={() => void generate("selected")}
          onSkip={() => {
            setDocumentIds([])
            void generate("skipped")
          }}
          onBack={() => setStep("role")}
        />
      )
      break
    case "personalization": {
      if (busy) {
        surface = (
          <div className="space-y-5">
            <PersonalizationProgress
              key={knowledgeMode}
              pagesReady={documentIds.length}
              phase={knowledgeMode === "selected" ? "personalization" : "role-prompt"}
              knowledgeSource="knowledge-base"
            />
            <WizardStepActions><Button variant="outline" onClick={back}><span>{t("back")}</span></Button></WizardStepActions>
          </div>
        )
        break
      }
      if (!result || !draft) {
        surface = (
          <WizardStepActions>
            <Button variant="outline" onClick={back}><span>{t("back")}</span></Button>
            <Button onClick={() => void generate(knowledgeMode)}><span>{t("retry")}</span></Button>
          </WizardStepActions>
        )
        break
      }
      surface = (
        <OnboardingStepActionsProvider backAction={{ disabled: create.isPending || regenerate.isPending, onClick: back }}>
          <PromptReviewForm
            Actions={WizardStepActions}
            agentName={identity.name}
            showAgentName={false}
            originalPrompt={draft.originalPrompt}
            enrichedPrompt={result.prompt}
            businessSummary={result.summary}
            languages={result.languages}
            knowledgeMode={knowledgeMode}
            personalizationWaiting={false}
            pagesReady={documentIds.length}
            personalizationError={null}
            onRetryPersonalization={() => void generate(knowledgeMode)}
            submitting={create.isPending}
            regenerating={regenerate.isPending}
            submitLabel={type === "voice" ? t("voiceNext") : t("create")}
            onRegenerate={async ({ businessFacts, languages }) => {
              const current = revision.current
              const generated = await regenerate.mutateAsync({ draftId: draft.draftId, businessFacts, languages })
              if (current !== revision.current || !mounted.current) return
              setResult({
                prompt: generated.enriched_prompt,
                summary: knowledgeMode === "selected" ? formatSignupOnboardingBusinessSummary(businessFacts) : null,
                languages,
                opening: generated.opening_message,
              })
            }}
            onApprove={async approved => {
              if (type === "voice") {
                if (voiceDraft && voiceDraft.languages.join(",") !== approved.languages.join(",")) setVoiceDraft(null)
                setApproval(approved)
                setResult({ ...result, prompt: approved.prompt, summary: approved.summary, languages: approved.languages })
                setStep("voice")
                return
              }
              await submit(approved)
            }}
          />
        </OnboardingStepActionsProvider>
      )
      break
    }
    case "voice":
      surface = result && approval ? (
        <SharedVoiceConfigurationForm
          Actions={WizardStepActions}
          voiceAvailability="provider"
          submitLabel={t("create")}
          languageEditing="locked"
          languages={approval.languages}
          initialVoiceId={voiceDraft?.voice_id}
          openingMessage={voiceDraft?.first_message ?? result.opening ?? ""}
          onBackToPrompt={values => {
            setVoiceDraft(values)
            setStep("personalization")
          }}
          onSubmit={async voice => submit({ ...approval, languages: voice.languages }, voice)}
        />
      ) : null
      break
    default: {
      const exhaustiveStep: never = step
      throw new Error(`Unsupported creation step: ${exhaustiveStep}`)
    }
  }
  const stepNumber = { role: 1, knowledge: 2, personalization: 3, voice: 3 }[step]
  return (
    <div className="mx-auto w-full max-w-4xl p-4 sm:p-6">
      <Card className="overflow-hidden">
        <div className="space-y-5 border-b p-4 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-xl font-semibold"><span>{t("title")}</span></h1>
            <Button variant="ghost" onClick={leave} disabled={create.isPending}><span>{t("cancel")}</span></Button>
          </div>
          <ol aria-label={t("steps")} className="grid gap-2 sm:grid-cols-3">
            {(["role", "knowledge", "personalization"] as const).map((value, index) => (
              <li key={value} aria-current={stepNumber === index + 1 ? "step" : undefined} className="min-w-0 rounded-lg border px-3 py-2 text-sm aria-[current=step]:border-blue-500 aria-[current=step]:bg-blue-50 aria-[current=step]:font-semibold aria-[current=step]:text-blue-700 dark:aria-[current=step]:border-blue-400 dark:aria-[current=step]:bg-blue-950/40 dark:aria-[current=step]:text-blue-300">
                <span>{t("step", { number: index + 1, label: t(value) })}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="p-4 sm:p-6">
          {error ? <Alert variant="destructive" className="mb-5"><AlertDescription>{error}</AlertDescription></Alert> : null}
          {surface}
        </div>
      </Card>
    </div>
  )
}
